import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useRef } from 'react';
import { loanService, adminService, documentService } from '../../services/loanService';
import { useAuth } from '../../hooks/useAuth';
import { LoanStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function toNum(v) { return parseFloat(v) || 0; }

function repaymentFrequency(tenureMonths, paymentCount) {
  if (!paymentCount || !tenureMonths) return '—';
  if (paymentCount === 1) return 'Lump sum';
  const days = Math.round((tenureMonths * 30) / paymentCount);
  if (days >= 28 && days <= 31) return 'Monthly';
  if (days >= 13 && days <= 15) return 'Bi-monthly';
  if (days >= 6  && days <= 8)  return 'Weekly';
  if (days === 1) return 'Daily';
  return `Every ${days} days`;
}

// ── Primitives ────────────────────────────────────────────────────────────────

function Card({ children, style }) {
  return <div className="card" style={{ marginBottom: '1rem', ...style }}>{children}</div>;
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.75rem' }}>
      {children}
    </div>
  );
}

function FR({ label, value, accent, mono, last }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem',
      padding: '0.35rem 0',
      borderBottom: last ? 'none' : '1px solid var(--border)',
    }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: '0.8rem', fontWeight: 600, textAlign: 'right',
        color: accent || 'var(--text-1)',
        fontFamily: mono ? 'monospace' : 'inherit',
      }}>{value ?? '—'}</span>
    </div>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--surface-alt)', borderRadius: 'var(--radius)', padding: '0.75rem', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: '1rem', fontWeight: 800, color: color || 'var(--text-1)', lineHeight: 1.2, wordBreak: 'break-word' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const MAP = {
    paid:    ['rgba(0,200,120,0.14)',  '#00C878', 'Paid'],
    overdue: ['rgba(255,71,87,0.14)',  '#FF4757', 'Overdue'],
    partial: ['rgba(245,166,35,0.14)', '#F5A623', 'Partial'],
    pending: ['rgba(255,255,255,0.07)','var(--text-3)', 'Pending'],
    waived:  ['rgba(91,142,255,0.14)', '#5B8EFF', 'Waived'],
  };
  const [bg, color, label] = MAP[status] || MAP.pending;
  return (
    <span style={{ padding: '1px 7px', borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, background: bg, color, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ── Offer actions ─────────────────────────────────────────────────────────────

function OfferActions({ loanId, onSuccess }) {
  const qc = useQueryClient();
  const [declining, setDeclining] = useState(false);
  const accept  = useMutation({
    mutationFn: () => loanService.acceptOffer(loanId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['loan-detail', loanId] }); onSuccess('Offer accepted! Disbursement will be processed within 24–48 hours.'); },
  });
  const decline = useMutation({
    mutationFn: () => loanService.declineOffer(loanId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['loan-detail', loanId] }); onSuccess('Offer declined.'); setDeclining(false); },
  });
  const busy = accept.isPending || decline.isPending;
  if (declining) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem', background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 'var(--radius)' }}>
      <span style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>Decline this offer?</span>
      <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', color: '#FF4757', borderColor: '#FF475740' }} disabled={busy} onClick={() => decline.mutate()}>
        {decline.isPending ? '…' : 'Yes'}
      </button>
      <button className="btn btn-ghost" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }} onClick={() => setDeclining(false)}>Cancel</button>
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '0.4rem 0.9rem' }} disabled={busy} onClick={() => accept.mutate()}>
        {accept.isPending ? 'Processing…' : 'Accept Offer'}
      </button>
      <button className="btn btn-ghost" style={{ fontSize: '0.82rem', padding: '0.4rem 0.9rem', color: '#FF4757', borderColor: '#FF475740' }} disabled={busy} onClick={() => setDeclining(true)}>
        Decline
      </button>
    </div>
  );
}

// ── Admin action modal (maker/staff only) ─────────────────────────────────────

function AdminActionModal({ loan, onClose, onSuccess }) {
  const qc = useQueryClient();
  const [action, setAction] = useState('under_review');
  const [amount, setAmount] = useState(loan.requested_amount || '');
  const [rate, setRate] = useState(loan.applied_interest_rate || '18');
  const [tenure, setTenure] = useState(loan.requested_tenure_months || '');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (d) => adminService.loanAction(loan.id, d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['loan-detail', loan.id] });
      if (r.data.approval_ref) {
        setResult({ ref: r.data.approval_ref, msg: r.data.message });
      } else {
        onSuccess?.('Action applied successfully.');
        onClose();
      }
    },
    onError: (e) => setErr(e.response?.data?.message || 'Action failed.'),
  });

  const handleSubmit = () => {
    const payload = { action, notes };
    if (action === 'approve') {
      payload.approved_amount = parseFloat(amount);
      payload.interest_rate = parseFloat(rate);
      payload.tenure_months = parseInt(tenure);
    }
    if (action === 'reject') payload.rejection_reason = rejectionReason;
    mutation.mutate(payload);
  };

  if (result) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 440, textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✓</div>
        <h2 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.5rem' }}>Submitted for Checker Review</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '1rem' }}>{result.msg}</p>
        <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 4 }}>Approval Reference</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: '1rem' }}>{result.ref}</div>
        </div>
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>Close</button>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 500, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Loan Action</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
          {loan.application_number} — {loan.applicant_name} — {formatCurrency(loan.requested_amount)}
        </p>
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <span>⚖</span>
          <span style={{ fontSize: '0.8rem' }}>Approve/Reject actions create a maker-checker request requiring a different admin to confirm.</span>
        </div>
        <div className="form-group">
          <label className="form-label">Action</label>
          <select className="form-input" value={action} onChange={e => setAction(e.target.value)}>
            <option value="under_review">Mark Under Review</option>
            <option value="request_docs">Request Additional Documents</option>
            <option value="approve">Recommend Approval (creates checker request)</option>
            <option value="reject">Recommend Rejection (creates checker request)</option>
          </select>
        </div>
        {action === 'approve' && (
          <>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Approved Amount (₦)</label>
                <input className="form-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Interest Rate (% p.m.)</label>
                <input className="form-input" type="number" step="0.1" value={rate} onChange={e => setRate(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Tenure (months)</label>
              <input className="form-input" type="number" value={tenure} onChange={e => setTenure(e.target.value)} />
            </div>
          </>
        )}
        {action === 'reject' && (
          <div className="form-group">
            <label className="form-label">Rejection Reason <span style={{ color: '#FF4757' }}>*</span></label>
            <textarea className="form-input" rows={3} value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Explain why this application is being rejected…" />
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Internal Notes</label>
          <textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes for this action…" />
        </div>
        {err && <div className="alert alert-error"><span>⚠</span><span>{err}</span></div>}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending} onClick={handleSubmit}>
            {mutation.isPending ? 'Processing…' : 'Submit Action'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Admin: Record Payment Modal ───────────────────────────────────────────────

function RecordPaymentModal({ loanId, schedule, onClose, onSuccess }) {
  const qc = useQueryClient();
  const remaining = parseFloat(schedule.amount_due) + parseFloat(schedule.late_fee || 0) - parseFloat(schedule.amount_paid || 0);
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [method, setMethod] = useState('bank_transfer');
  const [bankRef, setBankRef] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => adminService.recordPayment(loanId, schedule.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loan-detail', loanId] });
      qc.invalidateQueries({ queryKey: ['repayment-schedule', loanId] });
      onSuccess(`Payment of ₦${parseFloat(amount).toLocaleString()} recorded for Installment #${schedule.installment_number}.`);
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Failed to record payment.'),
  });

  const handleSubmit = () => {
    if (!amount || parseFloat(amount) <= 0) { setErr('Enter a valid amount.'); return; }
    const payload = { amount: parseFloat(amount), method };
    if (bankRef) payload.bank_reference = bankRef;
    if (paidAt) payload.paid_at = paidAt;
    if (notes) payload.notes = notes;
    mutation.mutate(payload);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 460, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Record Payment</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1rem' }}>
          Installment #{schedule.installment_number} · Due {formatDate(schedule.due_date)}
        </p>

        {/* Breakdown */}
        <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ color: 'var(--text-3)' }}>Amount Due</span>
            <span style={{ fontWeight: 700 }}>{formatCurrency(schedule.amount_due)}</span>
          </div>
          {parseFloat(schedule.late_fee || 0) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ color: '#FF4757' }}>Late Fee</span>
              <span style={{ fontWeight: 600, color: '#FF4757' }}>{formatCurrency(schedule.late_fee)}</span>
            </div>
          )}
          {parseFloat(schedule.amount_paid || 0) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ color: 'var(--text-3)' }}>Already Paid</span>
              <span style={{ color: '#00C878', fontWeight: 600 }}>{formatCurrency(schedule.amount_paid)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, borderTop: '1px solid var(--border)', marginTop: 4 }}>
            <span style={{ fontWeight: 600 }}>Remaining</span>
            <span style={{ fontWeight: 700, color: '#5B8EFF' }}>{formatCurrency(remaining)}</span>
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Amount (₦) <span style={{ color: '#FF4757' }}>*</span></label>
            <input className="form-input" type="number" step="0.01" min="0.01" max={remaining}
              value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select className="form-input" value={method} onChange={e => setMethod(e.target.value)}>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="card">Card Payment</option>
              <option value="ussd">USSD</option>
              <option value="direct_debit">Direct Debit</option>
              <option value="cash">Cash</option>
            </select>
          </div>
        </div>
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Bank / Payment Reference</label>
            <input className="form-input" placeholder="e.g. TXN123456789"
              value={bankRef} onChange={e => setBankRef(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Date Received</label>
            <input className="form-input" type="datetime-local"
              value={paidAt} onChange={e => setPaidAt(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Internal Notes</label>
          <textarea className="form-input" rows={2} placeholder="Optional notes about this payment…"
            value={notes} onChange={e => setNotes(e.target.value)} />
        </div>

        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending} onClick={handleSubmit}>
            {mutation.isPending ? 'Recording…' : 'Confirm & Record'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Admin: Maker Review Payment Modal ────────────────────────────────────────

function MakerReviewPaymentModal({ loanId, payment, onClose, onSuccess }) {
  const qc = useQueryClient();
  const [bankRef, setBankRef] = useState(payment.gateway_reference || '');
  const [paidAt, setPaidAt] = useState('');
  const [notes, setNotes] = useState(payment.notes || '');
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => adminService.makerReviewPayment(loanId, payment.id, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['loan-detail', loanId] });
      const msg = vars.decision === 'approved'
        ? `Payment of ${formatCurrency(payment.amount)} approved — awaiting checker confirmation.`
        : `Payment of ${formatCurrency(payment.amount)} declined.`;
      onSuccess(msg);
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Action failed.'),
  });

  const handleApprove = () => {
    const data = { decision: 'approved' };
    if (bankRef.trim()) data.gateway_reference = bankRef.trim();
    if (paidAt) data.paid_at = paidAt;
    if (notes.trim()) data.notes = notes.trim();
    mutation.mutate(data);
  };

  const handleDecline = () => mutation.mutate({ decision: 'declined' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 460, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Review Payment</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>

        {/* Payment summary */}
        <div style={{ background: 'rgba(91,142,255,0.07)', border: '1px solid rgba(91,142,255,0.2)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.82rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: 'var(--text-3)' }}>Borrower</span>
            <span style={{ fontWeight: 600 }}>{payment.payer_name || '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: 'var(--text-3)' }}>Amount</span>
            <span style={{ fontWeight: 700, color: '#00C878', fontSize: '0.95rem' }}>{formatCurrency(payment.amount)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: 'var(--text-3)' }}>Method</span>
            <span style={{ fontWeight: 600 }}>{(payment.method || '').replaceAll('_', ' ')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: 'var(--text-3)' }}>User Reference</span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{payment.reference}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-3)' }}>Submitted</span>
            <span>{formatDate(payment.paid_at || payment.created_at)}</span>
          </div>
        </div>

        {/* Transaction detail inputs */}
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Bank / Transaction Reference</label>
            <input className="form-input" placeholder="e.g. TXN123456789"
              value={bankRef} onChange={e => setBankRef(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Date Received</label>
            <input className="form-input" type="datetime-local"
              value={paidAt} onChange={e => setPaidAt(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={2} placeholder="Optional internal notes…"
            value={notes} onChange={e => setNotes(e.target.value)} />
        </div>

        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending} onClick={handleApprove}>
            {mutation.isPending ? 'Processing…' : '✓ Approve Payment'}
          </button>
          <button className="btn btn-ghost" style={{ color: '#FF4757', borderColor: '#FF475740' }} disabled={mutation.isPending} onClick={handleDecline}>
            Decline
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const ACTIONABLE_STATUSES = ['submitted', 'under_review', 'credit_check'];

// ── Requested Document Card ───────────────────────────────────────────────────

function DocRequestCard({ req, loanId }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');

  const fulfill = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return documentService.fulfillRequest(req.id, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loan-doc-requests', loanId] });
      qc.invalidateQueries({ queryKey: ['loan-detail', loanId] });
      setOpen(false);
      setErr('');
    },
    onError: (e) => setErr(e.response?.data?.message || 'Upload failed.'),
  });

  const isPending = req.status === 'pending';

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: '0.75rem' }}>
      <div style={{ padding: '0.75rem 1rem', background: 'var(--surface-alt)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{req.doc_type_display || req.doc_type?.replace(/_/g, ' ')}</span>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 99,
            background: isPending ? 'rgba(245,166,35,0.12)' : 'rgba(91,142,255,0.12)',
            color: isPending ? '#F5A623' : '#5B8EFF',
            border: `1px solid ${isPending ? 'rgba(245,166,35,0.25)' : 'rgba(91,142,255,0.25)'}` }}>
            {isPending ? 'Awaiting Upload' : 'Uploaded'}
          </span>
        </div>
        {isPending && (
          <button className="btn btn-primary" style={{ fontSize: '0.78rem', padding: '0.3rem 0.875rem' }}
            onClick={() => { setOpen(v => !v); setErr(''); }}>
            {open ? 'Cancel' : 'Upload Document'}
          </button>
        )}
        {!isPending && req.fulfilled_document_status && (
          <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 99,
            background: req.fulfilled_document_status === 'approved' ? 'rgba(0,200,150,0.12)' : 'rgba(255,71,87,0.12)',
            color: req.fulfilled_document_status === 'approved' ? '#00C896' : '#FF4757' }}>
            {req.fulfilled_document_status === 'approved' ? 'Approved' : 'Under Review'}
          </span>
        )}
      </div>
      {req.rejection_reason && (
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#FF4757', borderBottom: '1px solid var(--border)', background: 'rgba(255,71,87,0.04)' }}>
          <span style={{ fontWeight: 600 }}>Reason for resubmission: </span>{req.rejection_reason}
        </div>
      )}
      {req.notes && (
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}>
          <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>Instructions: </span>{req.notes}
        </div>
      )}
      {open && (
        <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {err && <div className="alert alert-error"><span>⚠</span><span>{err}</span></div>}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0, flex: 1 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Select file (PDF, JPG, PNG — max 10 MB)</label>
              <input className="form-input" type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png" />
            </div>
            <button className="btn btn-primary" disabled={fulfill.isPending}
              onClick={() => {
                const f = fileRef.current?.files?.[0];
                if (!f) { setErr('Please select a file.'); return; }
                fulfill.mutate(f);
              }}>
              {fulfill.isPending ? 'Uploading…' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LoanDetail() {
  const { id: loanId } = useParams();
  const { isStaff, isMaker, isChecker } = useAuth();
  const [banner, setBanner] = useState('');
  const [showAction, setShowAction] = useState(false);
  const [recordingSchedule, setRecordingSchedule] = useState(null);
  const [reviewingPayment, setReviewingPayment] = useState(null);

  const { data: loan, isLoading } = useQuery({
    queryKey: ['loan-detail', loanId],
    queryFn: () => loanService.getLoan(loanId).then(r => r.data.data),
  });

  const { data: docRequestsRaw } = useQuery({
    queryKey: ['loan-doc-requests', loanId],
    queryFn: () => documentService.getLoanDocumentRequests(loanId).then(r => r.data.data),
    enabled: !isStaff,
  });
  const docRequests = Array.isArray(docRequestsRaw) ? docRequestsRaw : (docRequestsRaw?.results || []);
  const pendingDocRequests = docRequests.filter(r => r.status === 'pending');

  const backTo   = isStaff ? '/admin/applications' : '/loans';
  const backLabel = isStaff ? 'Applications' : 'My Loans';

  if (isLoading) return <div style={{ padding: '3rem', textAlign: 'center' }}><LoadingSpinner /></div>;
  if (!loan) return (
    <div>
      <Link to={backTo} style={{ fontSize: '0.875rem', color: 'var(--text-3)' }}>← {backLabel}</Link>
      <p style={{ marginTop: '2rem', color: 'var(--text-3)' }}>Loan not found.</p>
    </div>
  );

  const offer    = loan.loan_offer ?? {};
  const hasOffer = Object.keys(offer).length > 0;
  const status   = loan.status ?? '';
  const schedule = loan.repayment_schedules ?? [];

  const totalPaid     = schedule.reduce((s, i) => s + toNum(i.amount_paid), 0);
  const totalDue      = schedule.reduce((s, i) => s + toNum(i.amount_due), 0);
  const totalLateFees = schedule.reduce((s, i) => s + toNum(i.late_fee), 0);
  const outstanding   = Math.max(0, toNum(offer.total_repayment || totalDue) + totalLateFees - totalPaid);
  const paidCount     = schedule.filter(i => i.status === 'paid').length;
  const overdueCount  = schedule.filter(i => i.status === 'overdue').length;
  const remaining     = schedule.filter(i => !['paid', 'waived'].includes(i.status)).length;
  const progress      = schedule.length > 0 ? paidCount / schedule.length : 0;
  const nextItem      = schedule.find(i => ['pending', 'partial', 'overdue'].includes(i.status));
  const paidItems     = schedule.filter(i => i.status === 'paid' && i.paid_at).sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at));
  const lastPaid      = paidItems[0] ?? null;
  const hasSchedule   = schedule.length > 0;
  const isOfferSent   = status === 'offer_sent';

  const tenureMonths = toNum(offer.tenure_months || loan.requested_tenure_months);
  const paymentCount = loan.payment_count ?? (schedule.length || 1);
  const frequency    = repaymentFrequency(tenureMonths, paymentCount);
  const interestRate = offer.interest_rate ?? loan.applied_interest_rate;
  const principalAmt = offer.approved_amount ?? loan.approved_amount ?? loan.requested_amount;

  const latePenaltyVal = hasOffer && offer.late_penalty_type
    ? offer.late_penalty_type === 'fixed'
      ? `${formatCurrency(offer.late_penalty_value)} flat`
      : `${offer.late_penalty_value}% of overdue`
    : '—';

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginBottom: '0.2rem' }}>
            <Link to={backTo} style={{ color: 'var(--text-3)', textDecoration: 'none' }}>{backLabel}</Link>
            {' / '}
            <span style={{ fontFamily: 'monospace' }}>{loan.application_number}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800 }}>{loan.application_number}</h1>
            <LoanStatusBadge status={status} />
            <span style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>
              {loan.product_name || loan.loan_type?.replace(/_/g, ' ')}
              {loan.submitted_at ? ` · ${formatDate(loan.submitted_at)}` : ''}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {!isStaff && isOfferSent && <OfferActions loanId={loanId} onSuccess={msg => { setBanner(msg); window.scrollTo(0, 0); }} />}
          {isStaff && isMaker && ACTIONABLE_STATUSES.includes(status) && (
            <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '0.4rem 0.9rem' }} onClick={() => setShowAction(true)}>
              Take Action
            </button>
          )}
        </div>
      </div>

      {/* ── Admin applicant banner ── */}
      {isStaff && (
        <div style={{
          display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center',
          padding: '0.625rem 1rem', marginBottom: '1rem',
          background: 'rgba(91,142,255,0.06)', border: '1px solid rgba(91,142,255,0.18)',
          borderRadius: 'var(--radius)', fontSize: '0.8rem',
        }}>
          <span style={{ color: 'var(--text-3)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Applicant</span>
          {loan.applicant_name && <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{loan.applicant_name}</span>}
          {loan.applicant_email && <span style={{ color: 'var(--text-2)' }}>{loan.applicant_email}</span>}
          {loan.applicant_tier && (
            <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 8px', borderRadius: 99, background: 'rgba(0,200,150,0.12)', color: '#00C896', textTransform: 'uppercase' }}>
              {loan.applicant_tier} tier
            </span>
          )}
        </div>
      )}

      {banner && (
        <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}>
          <span>✓</span><span>{banner}</span>
        </div>
      )}

      {/* ── Requested Documents (applicants only) ── */}
      {!isStaff && pendingDocRequests.length > 0 && (
        <Card style={{ borderLeft: '3px solid #F5A623', background: 'rgba(245,166,35,0.03)', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '1rem' }}>📄</span>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#F5A623', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Action Required
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-1)' }}>
                {pendingDocRequests.length} document{pendingDocRequests.length > 1 ? 's' : ''} requested — please upload to continue
              </div>
            </div>
          </div>
          {pendingDocRequests.map(req => (
            <DocRequestCard key={req.id} req={req} loanId={loanId} />
          ))}
        </Card>
      )}

      {/* ── KPI strip ── */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <Kpi label="Principal" value={formatCurrency(principalAmt)} color="#5B8EFF" />
        <Kpi label="Disbursed" value={hasOffer ? formatCurrency(offer.net_disbursement) : '—'} color="#00C896"
          sub={hasOffer ? `-₦${toNum(offer.processing_fee).toLocaleString()} fee` : undefined} />
        <Kpi label="Outstanding" value={formatCurrency(outstanding)} color={outstanding > 0 ? '#F5A623' : '#00C878'}
          sub={outstanding <= 0 && hasSchedule ? 'Fully repaid' : undefined} />
        <Kpi label="Total Repayable" value={hasOffer ? formatCurrency(offer.total_repayment) : '—'}
          sub={hasOffer ? `+${formatCurrency(offer.total_interest)} interest` : undefined} />
      </div>

      {/* ── Repayment progress bar (compact, inline) ── */}
      {hasSchedule && (
        <Card style={{ padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '0.3rem' }}>
            <span style={{ color: 'var(--text-2)' }}>
              <strong style={{ color: 'var(--text-1)' }}>{paidCount}</strong>/{schedule.length} paid
              {overdueCount > 0 && <span style={{ color: '#FF4757', marginLeft: '0.4rem' }}>· {overdueCount} overdue</span>}
              {remaining > 0 && <span style={{ color: 'var(--text-3)', marginLeft: '0.4rem' }}>· {remaining} left</span>}
            </span>
            <strong style={{ color: progress >= 1 ? '#00C878' : '#5B8EFF' }}>{Math.round(progress * 100)}%</strong>
          </div>
          <div style={{ height: 7, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress * 100}%`, background: progress >= 1 ? '#00C878' : 'linear-gradient(90deg,#5B8EFF,#00C896)', borderRadius: 4, transition: 'width 0.4s' }} />
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', fontSize: '0.72rem', color: 'var(--text-3)' }}>
            <span>Paid: <strong style={{ color: '#00C878' }}>{formatCurrency(totalPaid)}</strong></span>
            {totalLateFees > 0 && <span>Late fees: <strong style={{ color: '#FF4757' }}>{formatCurrency(totalLateFees)}</strong></span>}
          </div>
        </Card>
      )}

      {/* ── Two-column info grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

        {/* ── Left: Payment Activity ── */}
        <Card style={{ margin: 0 }}>
          <SectionTitle>Payment Activity</SectionTitle>
          <FR label="Next Payment Date"
            value={nextItem ? formatDate(nextItem.due_date) : '—'}
            accent={nextItem?.status === 'overdue' ? '#FF4757' : undefined} />
          <FR label="Next Payment Amount"
            value={nextItem ? formatCurrency(nextItem.amount_due) : '—'}
            accent={nextItem ? '#5B8EFF' : undefined} />
          {nextItem && toNum(nextItem.late_fee) > 0 && (
            <FR label="Late Fee on Next" value={formatCurrency(nextItem.late_fee)} accent="#FF4757" />
          )}
          <FR label="Total Paid So Far"      value={hasSchedule ? formatCurrency(totalPaid) : '—'} accent={totalPaid > 0 ? '#00C878' : undefined} />
          <FR label="Remaining Installments" value={hasSchedule ? `${remaining} of ${schedule.length}` : '—'} />
          <FR label="Last Payment Date"      value={lastPaid ? formatDate(lastPaid.paid_at) : '—'} />
          <FR label="Last Payment Amount"    value={lastPaid ? formatCurrency(lastPaid.amount_paid) : '—'} accent={lastPaid ? '#00C878' : undefined} last />
        </Card>

        {/* ── Right: Loan Terms ── */}
        <Card style={{ margin: 0 }}>
          <SectionTitle>Loan Terms</SectionTitle>
          <FR label="Loan Product"    value={loan.product_name || loan.loan_type?.replace(/_/g, ' ')} />
          <FR label="Reference"       value={loan.application_number} mono />
          <FR label="Currency"        value="NGN (₦)" />
          {loan.sanctioned_amount && (
            <FR label="Sanctioned Amt" value={formatCurrency(loan.sanctioned_amount)} />
          )}
          {loan.approved_amount && (
            <FR label="Approved Amt"
              value={formatCurrency(loan.approved_amount)}
              accent={toNum(loan.approved_amount) < toNum(loan.sanctioned_amount) ? '#F5A623' : '#00C896'} />
          )}
          <FR label="Interest Rate"   value={interestRate ? `${interestRate}% p.m.` : '—'} />
          <FR label="Interest Type"   value="Flat Rate" />
          <FR label="Tenor"           value={tenureMonths ? `${tenureMonths} month${tenureMonths !== 1 ? 's' : ''}` : '—'} />
          <FR label="Frequency"       value={frequency} />
          <FR label="Installments"    value={`${paymentCount}`} />
          <FR label="Processing Fee"  value={hasOffer ? formatCurrency(offer.processing_fee) : '—'} accent={hasOffer ? '#F5A623' : undefined} />
          <FR label="Late Penalty"    value={latePenaltyVal} accent={hasOffer && offer.late_penalty_type ? '#F5A623' : undefined} />
          <FR label="Default Charge"
            value={hasOffer && toNum(offer.default_charge_rate) > 0 ? `${offer.default_charge_rate}% p.m. after ${offer.default_threshold_days}d` : '—'}
            accent={hasOffer && toNum(offer.default_charge_rate) > 0 ? '#FF4757' : undefined} />
          <FR label="Date Applied"    value={formatDate(loan.submitted_at || loan.created_at)} />
          {loan.reviewed_at && <FR label="Reviewed On" value={formatDate(loan.reviewed_at)} />}
          {hasOffer && offer.expires_at && <FR label="Offer Expiry" value={formatDate(offer.expires_at)} />}
          {status === 'rejected' && loan.rejection_reason && (
            <FR label="Rejection" value={loan.rejection_reason} accent="#FF4757" />
          )}
          <FR label="Purpose" value={loan.purpose?.replace(/_/g, ' ')} last />
        </Card>
      </div>


      {/* ── Offer Terms (compact banner) ── */}
      {hasOffer && offer.offer_terms && (
        <Card style={{ borderLeft: '3px solid #5B8EFF', background: 'rgba(91,142,255,0.04)' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#5B8EFF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>Offer Terms</div>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.55 }}>{offer.offer_terms}</p>
        </Card>
      )}

      {/* ── Lender Notes ── */}
      {loan.notes && (
        <Card style={{ borderLeft: '3px solid #F5A623', background: 'rgba(245,166,35,0.04)' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#F5A623', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>Note from Lender</div>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.55 }}>{loan.notes}</p>
        </Card>
      )}

      {/* ── Status hint ── */}
      {!hasOffer && !hasSchedule && (
        <Card style={{
          borderLeft: `3px solid ${status === 'rejected' ? '#FF4757' : '#5B8EFF'}`,
          background: status === 'rejected' ? 'rgba(255,71,87,0.04)' : 'rgba(91,142,255,0.04)',
          marginBottom: '1rem',
        }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.55 }}>
            {status === 'submitted'
              ? '⏳ Application submitted — awaiting review.'
              : status === 'under_review'
              ? (loan.maker_recommended_at
                  ? '🔄 Your application has been reviewed and is now pending final sign-off by our approvals team.'
                  : '🔍 Being reviewed by our credit team.')
              : status === 'credit_check'
              ? '📊 Credit assessment in progress.'
              : status === 'offer_accepted'
              ? '✅ Approved — disbursement is being processed. No action needed.'
              : status === 'rejected'
              ? `❌ We regret to inform you that your application was not approved.${loan.rejection_reason ? ` Reason: ${loan.rejection_reason}` : ' Please contact us for more information.'}`
              : '⏳ Application being processed. You will be notified of updates.'}
          </p>
        </Card>
      )}

      {/* ── Full repayment schedule (full width) ── */}
      {hasSchedule && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <SectionTitle style={{ margin: 0 }}>Repayment Schedule — {schedule.length} installments</SectionTitle>
            {!isStaff && ['active','disbursed','closed','defaulted'].includes(status) && (
              <Link to={`/loans/${loanId}/repayments`} className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}>
                Make Payment →
              </Link>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
              <thead>
                <tr style={{ background: 'var(--surface-alt)' }}>
                  {['#','Due Date','Amount','Principal','Interest','Paid','Late Fee','Balance','Paid On','Status', ...(isStaff ? [''] : [])].map(h => (
                    <th key={h} style={{
                      padding: '0.4rem 0.6rem',
                      textAlign: h === '#' || h === 'Status' ? 'center' : 'right',
                      color: 'var(--text-3)', fontSize: '0.6rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                      borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, idx) => {
                  const isPaid    = row.status === 'paid';
                  const isOverdue = row.status === 'overdue';
                  const lateFee   = toNum(row.late_fee);
                  const paid      = toNum(row.amount_paid);
                  const rowPayments = row.payments || [];
                  const canRecord = isStaff && !isPaid;
                  const colSpan = isStaff ? 11 : 10;
                  return (
                    <>
                      <tr key={row.id || idx} style={{
                        borderBottom: rowPayments.length > 0 ? 'none' : '1px solid rgba(255,255,255,0.035)',
                        background: isOverdue ? 'rgba(255,71,87,0.03)' : isPaid ? 'rgba(0,200,120,0.02)' : 'transparent',
                      }}>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center', fontWeight: 800, fontSize: '0.72rem',
                          color: isPaid ? '#00C878' : isOverdue ? '#FF4757' : '#5B8EFF' }}>
                          {isPaid ? '✓' : row.installment_number}
                        </td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', whiteSpace: 'nowrap',
                          color: isOverdue ? '#FF4757' : 'var(--text-2)', fontWeight: isOverdue ? 600 : 400 }}>
                          {formatDate(row.due_date)}
                        </td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', fontWeight: 700, color: 'var(--text-1)',
                          textDecoration: isPaid ? 'line-through' : 'none', textDecorationColor: 'var(--text-3)' }}>
                          {formatCurrency(row.amount_due)}
                        </td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', color: 'var(--text-2)' }}>{formatCurrency(row.principal_due)}</td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', color: '#F5A623' }}>{formatCurrency(row.interest_due)}</td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', color: paid > 0 ? '#00C878' : 'var(--text-3)', fontWeight: paid > 0 ? 600 : 400 }}>
                          {paid > 0 ? formatCurrency(paid) : '—'}
                        </td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', color: lateFee > 0 ? '#FF4757' : 'var(--text-3)', fontWeight: lateFee > 0 ? 600 : 400 }}>
                          {lateFee > 0 ? formatCurrency(lateFee) : '—'}
                        </td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', color: isPaid ? 'var(--text-3)' : 'var(--text-2)' }}>
                          {formatCurrency(row.balance)}
                        </td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                          {row.paid_at ? formatDate(row.paid_at) : '—'}
                        </td>
                        <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center' }}><StatusPill status={row.status} /></td>
                        {isStaff && (
                          <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center' }}>
                            {canRecord && (
                              <button
                                className="btn btn-ghost"
                                style={{ fontSize: '0.68rem', padding: '0.2rem 0.5rem', whiteSpace: 'nowrap', color: 'var(--primary)', borderColor: 'rgba(0,200,150,0.3)' }}
                                onClick={() => setRecordingSchedule(row)}
                              >
                                Record
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {/* Payment receipts sub-rows */}
                      {rowPayments.map(p => (
                        <tr key={p.id} style={{ background: 'rgba(0,200,120,0.04)', borderBottom: '1px solid rgba(255,255,255,0.025)' }}>
                          <td />
                          <td colSpan={colSpan} style={{ padding: '0.3rem 0.6rem 0.3rem 1.2rem' }}>
                            <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.7rem', color: 'var(--text-3)' }}>
                              <span style={{ color: '#00C878', fontWeight: 700 }}>↳ {formatCurrency(p.amount)}</span>
                              <span>{p.method?.replace(/_/g, ' ')}</span>
                              {p.gateway_reference && <span style={{ fontFamily: 'monospace' }}>Ref: {p.gateway_reference}</span>}
                              <span>{p.paid_at ? formatDate(p.paid_at) : formatDate(p.created_at)}</span>
                              {p.payment_status === 'pending'
                                ? <span style={{ color: '#5B8EFF', fontWeight: 600 }}>⏳ Awaiting confirmation</span>
                                : p.recorded_by_admin
                                  ? <span style={{ color: '#818CF8' }}>Confirmed by {p.confirmed_by_name || 'Admin'}</span>
                                  : <span style={{ color: 'var(--text-3)' }}>Self-reported</span>
                              }
                              {p.notes && <span style={{ fontStyle: 'italic' }}>{p.notes}</span>}
                              {isMaker && p.payment_status === 'pending' && !p.maker_decision && (
                                <button
                                  className="btn btn-primary"
                                  style={{ fontSize: '0.65rem', padding: '0.15rem 0.5rem', marginLeft: 4 }}
                                  onClick={() => setReviewingPayment(p)}
                                >
                                  Review
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface-alt)', fontWeight: 700 }}>
                  <td colSpan={isStaff ? 3 : 2} style={{ padding: '0.4rem 0.6rem', fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 700 }}>Totals</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: 'var(--text-1)' }}>{formatCurrency(totalDue)}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: 'var(--text-2)' }}>{formatCurrency(schedule.reduce((s, i) => s + toNum(i.principal_due), 0))}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: '#F5A623' }}>{formatCurrency(schedule.reduce((s, i) => s + toNum(i.interest_due), 0))}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: '#00C878' }}>{formatCurrency(totalPaid)}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: totalLateFees > 0 ? '#FF4757' : 'var(--text-3)' }}>{formatCurrency(totalLateFees)}</td>
                  <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: outstanding > 0 ? '#F5A623' : '#00C878' }}>{formatCurrency(outstanding)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {showAction && (
        <AdminActionModal
          loan={loan}
          onClose={() => setShowAction(false)}
          onSuccess={msg => { setBanner(msg); setShowAction(false); window.scrollTo(0, 0); }}
        />
      )}

      {recordingSchedule && (
        <RecordPaymentModal
          loanId={loanId}
          schedule={recordingSchedule}
          onClose={() => setRecordingSchedule(null)}
          onSuccess={msg => { setBanner(msg); setRecordingSchedule(null); window.scrollTo(0, 0); }}
        />
      )}

      {reviewingPayment && (
        <MakerReviewPaymentModal
          loanId={loanId}
          payment={reviewingPayment}
          onClose={() => setReviewingPayment(null)}
          onSuccess={msg => { setBanner(msg); setReviewingPayment(null); window.scrollTo(0, 0); }}
        />
      )}
    </div>
  );
}
